import { FunctionComponent, useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Box, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import _ from 'lodash';
import clsx from 'clsx';
import { Button } from 'components/UI';
import { useSettingsMarketplace } from 'hooks';
import { Switch } from 'components/UI';
import { useToggle } from 'hooks';
import { useNonInitialEffect } from '@versiondos/hooks';
import { MarketplaceStatus } from '@spike/marketplace-action';
import { BusinessSettingsTitle } from '../UI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { SETTINGS_TRUE_VALUE } from '@spike/marketplace-model';

export interface Props {
    onSaveMarketplace: (tipBreakdownEnabled: boolean) => void;
    className?: string;
    openSidebar?: () => void;
    isMobile?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        mobileBody: {
            overflow: 'hidden'
        },
        container: {
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.only('lg')]: {
                width: '525px'
            },
            [theme.breakpoints.only('xl')]: {
                width: '565px'
            }
        },
        containerNew: {
            [theme.breakpoints.down('md')]: {
                'width': '100%',
                'height': '100%',
                'position': 'relative',
                'paddingBottom': '145px',
                'display': 'block',
                '@supports (-webkit-touch-callout: none)': {
                    paddingBottom: '160px'
                }
            }
        },
        titleContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        businessSettingsTittle: {
            'display': 'flex',
            'alignItems': 'center',
            '& $iconBusinessTitle': {
                fontSize: '20px'
            },
            [theme.breakpoints.down('sm')]: {
                'fontSize': '20px',
                'marginBottom': '25px',
                '& $iconBusinessTitle': {
                    fontSize: '20px',
                    fontWeight: '300'
                }
            }
        },
        iconBusinessTitle: {
            position: 'relative',
            marginRight: '8px'
        },
        contentContainer: {
            'display': 'flex',
            'flexDirection': 'column',
            'width': '100%',
            '& .MuiAccordionDetails-root': {
                padding: '0px'
            }
        },
        contentContainerMobile: {
            position: 'relative'
        },
        buttonContainer: {
            display: 'flex',
            width: '100%',
            [theme.breakpoints.down('sm')]: {
                marginTop: '27px'
            },
            [theme.breakpoints.only('md')]: {
                marginTop: '44px'
            },
            [theme.breakpoints.only('lg')]: {
                marginTop: '58px'
            },
            [theme.breakpoints.only('xl')]: {
                marginTop: '88px'
            }
        },
        tipBreakdownEnabled: {
            'display': 'flex',
            'justifyContent': 'space-between',
            [theme.breakpoints.down('sm')]: {
                marginBottom: '20px',
                borderBottom: 'none'
            },
            [theme.breakpoints.only('md')]: {
                marginBottom: '17px',
                borderBottom: 'none'
            },
            [theme.breakpoints.only('lg')]: {
                marginBottom: '22px'
            },
            [theme.breakpoints.only('xl')]: {
                marginBottom: '34px'
            },
            '& .MuiTypography-root': {
                fontWeight: 500,
                [theme.breakpoints.down('sm')]: {
                    fontSize: '16px',
                    fontWeight: 500,
                    lineHeight: '100%',
                    marginBottom: '3px'
                },
                [theme.breakpoints.only('md')]: {
                    fontSize: '16px',
                    lineHeight: '15px',
                    marginBottom: '5px'
                },
                [theme.breakpoints.only('lg')]: {
                    fontSize: '16px',
                    lineHeight: '20px',
                    marginBottom: '7px'
                },
                [theme.breakpoints.only('xl')]: {
                    fontSize: '16px',
                    lineHeight: '30px',
                    marginBottom: '11px'
                }
            }
        },
        buttonContainerFull: {
            [theme.breakpoints.down('sm')]: {
                'width': '100%',
                'position': 'absolute',
                'bottom': '60px',
                'left': 0,
                'paddingLeft': '15px',
                'paddingRight': '15px',
                '& button': {
                    'width': '100%',
                    'height': '50px',
                    'borderRadius': '31px',
                    '& .MuiButton-label': {
                        fontSize: '16px'
                    }
                },
                '@supports (-webkit-touch-callout: none)': {
                    bottom: '75px'
                }
            }
        }
    })
);

export const Invoices: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const marketplace = useSettingsMarketplace();

    const status = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);

    const [saved, setSaved] = useState(false);
    const [loading, setLoading] = useState(false);

    const [tipBreakdownEnabled, handleTipBreakdownEnabled] = useToggle(
        marketplace.settings?.find(s => s.key === 'tip_breakdown_enabled')?.value === SETTINGS_TRUE_VALUE
    );

    useNonInitialEffect(() => {
        switch (status) {
            case MarketplaceStatus.SaveSettingsMarketplaceSuccess:
            case MarketplaceStatus.SaveSuccess:
                setSaved(true);
                setLoading(false);
                break;
            case MarketplaceStatus.Error:
                setLoading(false);
        }
    }, [status]);

    const saveHandler = () => {
        setLoading(true);
        props.onSaveMarketplace && props.onSaveMarketplace(tipBreakdownEnabled);
    };

    return (
        <Box className={clsx(classes.container, props.className, classes.containerNew)}>
            <Box className={classes.titleContainer}>
                <BusinessSettingsTitle
                    className={classes.businessSettingsTittle}
                    subtitle="Manage your invoice settings and preferences."
                >
                    {' '}
                    {props.isMobile && (
                        <FontAwesomeIcon
                            className={classes.iconBusinessTitle}
                            onClick={props.openSidebar}
                            icon={faArrowLeft}
                            size="2x"
                        />
                    )}{' '}
                    Invoices
                </BusinessSettingsTitle>
            </Box>
            <Box className={clsx(classes.contentContainer, classes.contentContainerMobile)}>
                <Box
                    className={classes.tipBreakdownEnabled}
                    onClick={handleTipBreakdownEnabled}
                >
                    <Typography>Show tip breakdown by staff on client invoices</Typography>
                    <Switch
                        checked={tipBreakdownEnabled}
                        onChange={handleTipBreakdownEnabled}
                    />
                </Box>
            </Box>
            <Box className={clsx(classes.buttonContainer, classes.buttonContainerFull)}>
                <Button
                    label={saved ? 'Saved' : 'Save'}
                    onClick={saveHandler}
                    loading={loading}
                />
            </Box>
        </Box>
    );
};

export default Invoices;
